import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const SHORTCUTS = [
  { section: "Nawigacja", items: [
    { keys: ["G", "D"], label: "Dashboard" , path: "/" },
    { keys: ["G", "K"], label: "Klienci" , path: "/klienci" },
    { keys: ["G", "Z"], label: "Zadania" , path: "/zadania" },
    { keys: ["G", "F"], label: "Faktury" , path: "/faktury" },
    { keys: ["G", "E"], label: "Email" , path: "/e-maile" },
    { keys: ["G", "C"], label: "Kalendarz" , path: "/kalendarz" },
    { keys: ["G", "R"], label: "Raporty" , path: "/raporty" },
    { keys: ["G", "U"], label: "Umowy" , path: "/umowy" },
    { keys: ["G", "A"], label: "Aneksy" , path: "/aneksy" },
    { keys: ["G", "J"], label: "JPK Konwerter" , path: "/jpk" },
    { keys: ["G", "W"], label: "Kalkulator wynagrodzen" , path: "/kalkulator-wynagrodzen" },
    { keys: ["G", "S"], label: "Ustawienia" , path: "/ustawienia" },
  ]},
  { section: "Akcje", items: [
    { keys: ["N"], label: "Nowy klient" , action: "client" },
    { keys: ["T"], label: "Nowe zadanie" , action: "task" },
    { keys: ["Ctrl", "K"], label: "Szukaj" , action: "search" },
    { keys: ["?"], label: "Pomoc — skroty klawiaturowe" , action: "help" },
  ]},
];

export function KeyboardShortcuts({ onQuickAction }) {
  const [showHelp, setShowHelp] = useState(false);
  const [gPressed, setGPressed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let gTimeout;

    const handleKeyDown = (e) => {
      const tag = e.target.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target.isContentEditable) return;

      // Show help with "?"
      if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowHelp(prev => !prev);
        return;
      }

      // Handle G + key combos
      if (e.key === "g" || e.key === "G") {
        if (!e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          setGPressed(true);
          clearTimeout(gTimeout);
          gTimeout = setTimeout(() => setGPressed(false), 1500);
          return;
        }
      }

      if (gPressed) {
        setGPressed(false);
        clearTimeout(gTimeout);
        const key = e.key.toLowerCase();
        const routeMap = { d: "/", k: "/klienci", z: "/zadania", f: "/faktury", e: "/e-maile", c: "/kalendarz", r: "/raporty", u: "/umowy", a: "/aneksy", j: "/jpk", w: "/kalkulator-wynagrodzen", s: "/ustawienia" };
        if (routeMap[key]) {
          e.preventDefault();
          navigate(routeMap[key]);
          return;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(gTimeout);
    };
  }, [gPressed, navigate, onQuickAction]);

  return (
    <>
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg" data-testid="shortcuts-title">Skroty klawiaturowe</DialogTitle>
          </DialogHeader>
          <div className="space-y-6" data-testid="shortcuts-list">
            {SHORTCUTS.map(section => (
              <div key={section.section}>
                <h3 className="text-xs font-semibold text-slate-400 uppercase mb-2">{section.section}</h3>
                <div className="space-y-1.5">
                  {section.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50">
                      <span className="text-sm text-slate-700">{item.label}</span>
                      <div className="flex items-center gap-1">
                        {item.keys.map((k, j) => (
                          <React.Fragment key={j}>
                            {j > 0 && <span className="text-slate-300 text-xs">+</span>}
                            <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-slate-50">{k}</Badge>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <span className="text-[10px] text-slate-400 text-center mt-2 block">Nacisnij <Badge variant="outline" className="text-[9px] font-mono px-1 rounded-md">?</Badge> aby zamknac</span>
        </DialogContent>
      </Dialog>

      {/* G-mode indicator */}
      {gPressed && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] bg-[#0A1C3A] text-white px-4 py-2 rounded-full text-sm font-medium shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="g-mode-indicator">
          G + ... (nacisnij klawisz docelowy)
        </div>
      )}
    </>
  );
}
